import { loadEmployees, saveEmployees } from './employeeData';
import { saveSyncData, hasSyncData, isSyncStale, getSyncedMembers, getSyncedBoards, clearSyncData } from './syncStore';

const API_KEY = process.env.REACT_APP_TRELLO_API_KEY;
const TOKEN   = process.env.REACT_APP_TRELLO_TOKEN;
const BASE    = 'https://api.trello.com/1';
const auth    = () => `key=${API_KEY}&token=${TOKEN}`;
const delay   = ms => new Promise(r => setTimeout(r, ms));

async function fetchJSON(url) {
  const res = await fetch(url);
  if (res.status === 429) throw new Error('rate_limit');
  if (!res.ok) return null;
  return res.json();
}

async function fetchWithRetry(url, retries = 3) {
  for (let i = 0; i < retries; i++) {
    try { return await fetchJSON(url); }
    catch (e) {
      if (e.message === 'rate_limit') await delay(2000 * (i + 1));
      else throw e;
    }
  }
  return null;
}

export async function syncGetBoards() {
  return fetchWithRetry(
    `${BASE}/members/me/boards?filter=open&fields=id,name,desc,prefs,labelNames&${auth()}`
  );
}

export async function syncGetBoardMembers(boardId) {
  return fetchWithRetry(
    `${BASE}/boards/${boardId}/members?fields=id,fullName,username,avatarUrl&${auth()}`
  );
}

export async function syncGetBoardLists(boardId) {
  return fetchWithRetry(
    `${BASE}/boards/${boardId}/lists?fields=id,name&${auth()}`
  );
}

export async function runTrelloSync(onProgress, { force = false } = {}) {
  if (!force && hasSyncData() && !isSyncStale()) {
    onProgress?.('Using cached data…');
    const boards  = getSyncedBoards();
    const members = getSyncedMembers();
    const result  = linkEmployees(members);
    return { boards, allMembers: members, ...result, fromCache: true };
  }

  // Step 1 — boards
  onProgress?.('Loading boards…');
  const boards = await syncGetBoards();

  // Step 2 — scan all boards sequentially, collect unique members by ID
  // Note: Trello does NOT return emails for other members — name matching only
  const memberMap = {};
  for (let i = 0; i < boards.length; i++) {
    onProgress?.(`Scanning board ${i + 1} of ${boards.length}…`);
    const members = await syncGetBoardMembers(boards[i].id);
    if (members) {
      members.forEach(m => { if (!memberMap[m.id]) memberMap[m.id] = m; });
    }
    await delay(200);
  }

  const allMembers = Object.values(memberMap);
  onProgress?.(`Found ${allMembers.length} unique members. Matching to directory…`);

  // Debug: log what Trello names look like vs our employee names
  const employees = loadEmployees();
  console.log('=== TRELLO MEMBERS ===');
  console.table(allMembers.map(m => ({ id: m.id, fullName: m.fullName, username: m.username })));
  console.log('=== EMPLOYEES (unlinked) ===');
  console.table(employees.filter(e => !e.trelloId).map(e => ({
    name: `${e.firstName} ${e.lastName}`,
    email: e.email,
  })));

  // Detect Trello members not in employee directory at all
  const linkedTrelloIds = new Set(employees.filter(e => e.trelloId).map(e => e.trelloId));
  const newMembers = allMembers.filter(m => !linkedTrelloIds.has(m.id));

  saveSyncData({ boards, members: allMembers, newMembers });
  const result = linkEmployees(allMembers);

  // After linking, recalculate new members (some may have just been linked by name)
  const stillLinkedIds = new Set(result.employees.filter(e => e.trelloId).map(e => e.trelloId));
  const trueNewMembers = allMembers.filter(m => !stillLinkedIds.has(m.id));

  // Update store with accurate new members list
  saveSyncData({ boards, members: allMembers, newMembers: trueNewMembers });

  return { boards, allMembers, ...result, newMembers: trueNewMembers, fromCache: false };
}

function normalize(str) {
  return (str || '').toLowerCase().trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // handle accents
}

function linkEmployees(trelloMembers) {
  const employees = loadEmployees();
  let linked = 0, alreadyLinked = 0, unmatched = 0;
  const linkedNames = [];

  const updated = employees.map(emp => {
    if (emp.trelloId) {
      if (trelloMembers.find(m => m.id === emp.trelloId)) { alreadyLinked++; return emp; }
    }

    const empFull  = normalize(`${emp.firstName} ${emp.lastName}`);
    const empFirst = normalize(emp.firstName);
    const empLast  = normalize(emp.lastName);

    // Match 1: exact full name
    let match = trelloMembers.find(m => normalize(m.fullName) === empFull);

    // Match 2: Trello fullName contains both first and last
    if (!match) match = trelloMembers.find(m => {
      const n = normalize(m.fullName);
      return n.includes(empFirst) && n.includes(empLast);
    });

    // Match 3: username contains first+last (e.g. "chadcarvin")
    if (!match) match = trelloMembers.find(m => {
      const u = normalize(m.username);
      return u.includes(empFirst) && u.includes(empLast.slice(0, 4));
    });

    if (match) {
      linked++;
      linkedNames.push(`${emp.firstName} ${emp.lastName}`);
      return { ...emp, trelloId: match.id };
    }

    unmatched++;
    return emp;
  });

  saveEmployees(updated);
  return { linked, alreadyLinked, unmatched, linkedNames, employees: updated };
}
